import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Download, FileText } from 'lucide-react';
import AvvCard from './AvvCard';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

/**
 * "Rechtliches" — Impressum + Datenschutz + AVV + DSGVO Art. 20 export.
 * Patient data export is admin-only and strictly scoped to their own
 * practice (backend enforces). Superadmin sees no export button.
 */
export default function LegalSection({ form, setF, t }) {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const canExport = user?.role === 'admin';

  async function exportCsv() {
    setExporting(true);
    try {
      const tok = localStorage.getItem('studioops_access_token');
      const pid = localStorage.getItem('studioops_active_practice');
      const headers = { Authorization: `Bearer ${tok}` };
      if (pid) headers['X-Practice-Id'] = pid;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dsgvo/waitlist-export`, { headers });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.message || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      // Server already set Content-Disposition with a sensible filename; just
      // honour it. If not present we fall back to a sane default.
      const cd = r.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `patientendaten_${new Date().toISOString().slice(0,10)}.csv`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 500);
      toast.success(t('settings.legal.exportSuccess'));
    } catch (err) {
      toast.error(err.message || t('settings.legal.exportFailed'));
    } finally { setExporting(false); }
  }

  return (
    <div className="so-card p-5 lg:col-span-2" data-testid="legal-section">
      <h2 className="text-sm font-semibold mb-4">{t('settings.sections.legal')}</h2>
      <div className="rounded-md bg-accent/5 border border-accent/20 p-3 mb-4 text-xs text-text-secondary leading-relaxed" data-testid="rechtliches-info-box">
        <strong className="text-accent">{t('settings.legal.whatIsAvv')}</strong><br/>
        {t('settings.legal.avvDescription')}
      </div>

      <AvvCard className="mb-5" t={t}/>

      {canExport && (
        <div className="rounded-md border border-border bg-background/40 p-4 mb-5" data-testid="dsgvo-export-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-semibold flex items-center gap-2"><FileText size={14} className="text-accent"/> {t('settings.legal.dsgvoExportTitle')}</p>
              <p className="text-xs text-text-secondary mt-1">{t('settings.legal.dsgvoExportHint')}</p>
            </div>
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="so-btn so-btn-secondary text-xs"
              data-testid="dsgvo-export-btn"
            >
              <Download size={12}/>{exporting ? t('common.loading') : t('settings.legal.dsgvoExportBtn')}
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="so-label">{t('settings.legal.impressumLabel')}</label>
          <p className="text-[11px] text-text-secondary mb-1.5 -mt-0.5">{t('settings.legal.impressumHint')}</p>
          <textarea
            rows={9}
            className="so-input font-sans text-sm leading-relaxed"
            placeholder={t('settings.legal.impressumPlaceholder')}
            value={form.impressum || ''}
            onChange={(e) => setF('impressum', e.target.value)}
            data-testid="set-impressum"
          />
        </div>
        <div>
          <label className="so-label">{t('settings.legal.datenschutzLabel')}</label>
          <p className="text-[11px] text-text-secondary mb-1.5 -mt-0.5">{t('settings.legal.datenschutzHint')}</p>
          <textarea
            rows={9}
            className="so-input font-sans text-sm leading-relaxed"
            placeholder={t('settings.legal.datenschutzPlaceholder')}
            value={form.datenschutz || ''}
            onChange={(e) => setF('datenschutz', e.target.value)}
            data-testid="set-datenschutz"
          />
        </div>
      </div>
    </div>
  );
}
